import { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';
import { AbstractElementCommand } from './AbstractCommand';
import { TimelineMode } from './TimelineMode';
import { KaraokeTrackManager } from '../lib/KaraokeTrackManager';

import type { Element } from '../models/Element';
import type { VideoEditor } from '../VideoEditor';

/**
 * Command to trim the start of an element (adjusts start time and duration)
 * Uses direct object references
 */
export class TrimElementStartCommand extends AbstractElementCommand {
  private newStartTime: number;
  private snapToFrame: boolean;

  getName(): string {
    return 'TrimElementStartCommand';
  }
  private originalState: Record<string, any>;
  /**
   * Creates a new TrimElementStartCommand
   * @param editor The video editor instance
   * @param element The element to trim
   * @param newStartTime The new start time for the element (in seconds)
   */
  constructor(
    editor: VideoEditor,
    element: Element,
    newStartTime: number,
    snapToFrame: boolean = true,
  ) {
    super(editor, element);

    // Ensure time is never negative and apply frame snapping
    this.newStartTime = Math.max(
      0,
      snapToFrame ? this.snapTimeToFrame(newStartTime) : newStartTime,
    );
    this.snapToFrame = snapToFrame;

    // Capture full element state for undo
    this.originalState = this.captureElementState(this.sourceElement);

    // Store affected elements for undo in ripple mode
    if (this.getTimelineMode() === TimelineMode.RIPPLE) {
      this.storeAffectedElements({});
    }
  }

  execute(): boolean {
    const isTranscriptElem = this.isTranscriptElement();
    const result = this.performTrimOperation();

    if (result) {
      this.recalculateTimeline();

      // Reconcile karaoke track if transcript element was trimmed
      if (isTranscriptElem) {
        const karaokeTrack = this.editor.getKaraokeTrack();
        if (karaokeTrack) {
          const karaokeTrackManager = new KaraokeTrackManager(this.editor);
          karaokeTrackManager.reconcileKaraokeTrack();
        }
      }
    }

    return result;
  }

  /**
   * Performs the trim operation using the appropriate track manager
   * @returns true if the operation was successful
   */
  private performTrimOperation(): boolean {
    if (this.isTranscriptElement()) {
      return this.trimTranscriptElement();
    } else {
      return this.trimRegularElement();
    }
  }

  /**
   * Checks if the source element is a VideoTranscriptElement
   */
  private isTranscriptElement(): boolean {
    return this.sourceElement instanceof VideoTranscriptElement;
  }

  /**
   * Trims a VideoTranscriptElement using TranscriptTrackManager
   */
  private trimTranscriptElement(): boolean {
    const transcriptTrackManager = this.editor.getTranscriptTrackManager();
    const clipResult = transcriptTrackManager.trimElementStart(
      this.sourceElement as VideoTranscriptElement,
      this.newStartTime,
      this.getTimelineMode(),
      this.snapToFrame,
    );
    return clipResult !== null;
  }

  /**
   * Trims a regular element using TrackManager
   */
  private trimRegularElement(): boolean {
    const trackManager = this.editor.getTrackManager();
    return trackManager.trimElementStart(
      this.sourceElement,
      this.newStartTime,
      this.getTimelineMode(),
      this.snapToFrame,
    );
  }

  undo(): boolean {
    // Restore the complete original element state
    this.restoreElementState(this.sourceElement, this.originalState);

    // Restore affected elements if in ripple mode
    this.restoreAffectedElements();

    // Recalculate timeline duration
    this.recalculateTimeline();

    return true;
  }

  getDescription(): string {
    return `Trim start of element ${this.sourceElement.id} to ${this.newStartTime}s`;
  }
}
